import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { LogOut, Gift, Package, Truck, Copy } from "lucide-react";

export default function Account() {
  const nav = useNavigate();
  const { token, customer, logout, loading, setCustomer } = useAuth();
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [benefit, setBenefit] = useState(null);
  const [profile, setProfile] = useState({ name: "", phone: "", birthday: "" });

  useEffect(() => {
    if (!loading && !token) { nav("/login"); return; }
    if (!token) return;
    const h = { headers: { Authorization: `Bearer ${token}` } };
    api.get("/customer/orders", h).then((r) => setOrders(r.data));
    api.get("/customer/products", h).then((r) => setProducts(r.data));
    api.get("/customer/birthday-benefit", h).then((r) => setBenefit(r.data));
  }, [token, loading, nav]);

  useEffect(() => {
    if (customer) setProfile({ name: customer.name || "", phone: customer.phone || "", birthday: customer.birthday || "" });
  }, [customer]);

  if (loading || !customer) return <div className="min-h-[60vh] flex items-center justify-center text-white/60">Loading…</div>;

  const saveProfile = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.put("/customer/me", profile, { headers: { Authorization: `Bearer ${token}` } });
      setCustomer(data);
      toast.success("Profile updated.");
      // refresh benefit if birthday changed
      const b = await api.get("/customer/birthday-benefit", { headers: { Authorization: `Bearer ${token}` } });
      setBenefit(b.data);
    } catch { toast.error("Failed to update."); }
  };

  const doLogout = () => { logout(); nav("/"); };

  const copyCoupon = () => { if (benefit?.coupon_code) { navigator.clipboard.writeText(benefit.coupon_code); toast.success("Coupon copied."); } };

  return (
    <div className="text-white max-w-6xl mx-auto px-6 md:px-10 py-16">
      <div className="flex justify-between items-start flex-wrap gap-4 mb-8">
        <div>
          <p className="uppercase text-xs tracking-[0.3em] text-[#00B5D8] mb-3">My Account</p>
          <h1 className="font-display text-4xl md:text-5xl">Hello, {customer.name.split(" ")[0]}.</h1>
          <p className="mt-2 text-white/60 text-sm">{customer.email}</p>
        </div>
        <Button variant="ghost" onClick={doLogout} className="text-white/70 hover:text-white gap-2" data-testid="account-logout"><LogOut size={16} /> Sign out</Button>
      </div>

      {benefit?.eligible && (
        <div className="mb-8 bg-gradient-to-br from-[#00B5D8]/20 to-[#00B5D8]/5 border border-[#00B5D8]/40 rounded-3xl p-6 flex items-start gap-4">
          <Gift size={28} className="text-[#00B5D8] shrink-0 mt-1" />
          <div className="flex-1">
            <p className="font-display text-2xl">🎉 It's your birthday month!</p>
            <p className="mt-2 text-white/80">Enjoy <span className="text-[#00B5D8] font-bold">15% OFF accessories</span> on one purchase this month. Expires {new Date(benefit.expires_at).toLocaleDateString()}.</p>
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <code className="bg-black/50 border border-white/10 rounded-full px-3 py-1 text-sm font-mono" data-testid="birthday-coupon">{benefit.coupon_code}</code>
              <button onClick={copyCoupon} className="text-xs bg-white/10 hover:bg-white/20 rounded-full px-3 py-1 flex items-center gap-1 transition-colors"><Copy size={12} /> Copy</button>
              <Link to="/shop" className="preshh-btn-primary text-sm">Shop Accessories</Link>
            </div>
          </div>
        </div>
      )}

      <Tabs defaultValue="overview">
        <TabsList className="bg-[#111] border border-white/10">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders">Orders ({orders.length})</TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products">My Products ({products.length})</TabsTrigger>
          <TabsTrigger value="referral" data-testid="tab-referral">Refer a friend</TabsTrigger>
          <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6 grid md:grid-cols-3 gap-4">
          <div className="bg-[#111] border border-white/10 rounded-2xl p-6">
            <Package size={22} className="text-[#00B5D8]" />
            <p className="mt-3 text-3xl font-display">{orders.length}</p>
            <p className="text-sm text-white/60">Total orders</p>
          </div>
          <div className="bg-[#111] border border-white/10 rounded-2xl p-6">
            <Truck size={22} className="text-[#00B5D8]" />
            <p className="mt-3 text-3xl font-display">{products.reduce((s, p) => s + p.quantity, 0)}</p>
            <p className="text-sm text-white/60">Preshh products owned</p>
          </div>
          <div className="bg-[#111] border border-white/10 rounded-2xl p-6">
            <Gift size={22} className="text-[#00B5D8]" />
            <p className="mt-3 text-3xl font-display">{
              benefit?.eligible ? "Active" :
              benefit?.reason === "already_used_this_year" ? "Used" :
              benefit?.reason === "not_birthday_month" ? "Off-month" :
              "Set birthday"
            }</p>
            <p className="text-sm text-white/60">Birthday benefit</p>
          </div>
        </TabsContent>

        <TabsContent value="orders" className="mt-6 space-y-3">
          {orders.length === 0 && <p className="text-white/50 py-8 text-center">No orders yet. <Link to="/preshhgo" className="text-[#00B5D8] underline">Shop Preshh GO</Link></p>}
          {orders.map((o) => (
            <div key={o.id} className="bg-[#111] border border-white/10 rounded-2xl p-5">
              <div className="flex flex-wrap justify-between gap-4">
                <div>
                  <p className="text-xs text-white/50">{new Date(o.created_at).toLocaleString()}</p>
                  <p className="text-xs font-mono text-white/60" data-testid={`account-order-no-${o.id}`}>Order {o.order_no || o.id.slice(0, 8)}</p>
                  <p className="mt-2 text-sm text-white/80">{o.items.map((i) => `${i.name} × ${i.quantity}`).join(" · ")}</p>
                </div>
                <div className="text-right">
                  <p className="font-display text-2xl">{money(o.amount, o.currency)}</p>
                  <p className="text-xs uppercase text-[#00B5D8]">{o.status}</p>
                </div>
              </div>
              {o.tracking_number && (
                <p className="mt-3 text-xs text-white/70">Tracking: <span className="font-mono">{o.tracking_number}</span></p>
              )}
              {o.discount?.code && <p className="mt-2 text-xs text-[#00B5D8]">🎁 Birthday discount applied: −{money(o.discount.amount, o.currency)}</p>}
            </div>
          ))}
        </TabsContent>

        <TabsContent value="products" className="mt-6 grid md:grid-cols-2 gap-4">
          {products.length === 0 && <p className="text-white/50 py-8 text-center md:col-span-2">No products owned yet.</p>}
          {products.map((p) => (
            <div key={p.slug} className="bg-[#111] border border-white/10 rounded-2xl p-4 flex gap-4">
              {p.image && <img src={p.image} alt="" className="w-24 h-24 rounded-lg object-cover shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="font-semibold">{p.name}</p>
                <p className="text-xs text-white/50 uppercase">{p.category}</p>
                <p className="text-sm text-white/70 mt-1">Quantity owned: <span className="text-white">{p.quantity}</span></p>
                <p className="text-xs text-white/50 mt-1">First purchased: {new Date(p.first_purchase_at).toLocaleDateString()}</p>
                <div className="mt-4">
                  <p className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Personal support kit</p>
                  <div className="flex gap-2 flex-wrap">
                    <Link to={`/product/${p.slug}`} className="text-xs bg-white/10 hover:bg-white/15 rounded-full px-3 py-1 transition-colors">View product</Link>
                    <a href={p.manual_url || "/manuals/preshh-go-user-manual.pdf"} target="_blank" rel="noreferrer" className="text-xs bg-[#00B5D8]/20 hover:bg-[#00B5D8]/30 text-[#00B5D8] rounded-full px-3 py-1 transition-colors" data-testid={`product-manual-${p.slug}`}>View Manual</a>
                    {p.video_url ? (
                      <a href={p.video_url} target="_blank" rel="noreferrer" className="text-xs bg-white/10 hover:bg-white/15 rounded-full px-3 py-1 transition-colors">Watch Setup Video</a>
                    ) : (
                      <span className="text-xs bg-white/5 text-white/40 rounded-full px-3 py-1 italic">Setup video coming soon</span>
                    )}
                    {p.category !== "accessory" && <Link to="/shop" className="text-xs bg-white/10 hover:bg-white/15 rounded-full px-3 py-1 transition-colors">Compatible accessories</Link>}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="referral" className="mt-6 max-w-2xl">
          <div className="bg-gradient-to-br from-[#00B5D8]/15 to-[#00B5D8]/5 border border-[#00B5D8]/30 rounded-3xl p-6 md:p-8">
            <p className="uppercase text-xs tracking-[0.3em] text-[#00B5D8]">Refer a friend</p>
            <h3 className="mt-2 font-display text-3xl">Give 10%. Get €20 in accessories.</h3>
            <p className="mt-3 text-white/70 text-sm">Share your personal code — friends get <span className="text-white">10% off</span> their first Preshh GO. When they buy, you get <span className="text-white">€20 in Preshh accessory credit</span>.</p>
            {customer.referral_code && (
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <code data-testid="referral-code" className="bg-black/60 border border-white/10 rounded-full px-4 py-2 font-mono text-lg">{customer.referral_code}</code>
                <button onClick={() => { navigator.clipboard.writeText(customer.referral_code); toast.success("Code copied."); }} className="text-xs bg-white/10 hover:bg-white/20 rounded-full px-3 py-2 flex items-center gap-1 transition-colors">
                  <Copy size={12} /> Copy code
                </button>
                <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/preshhgo?ref=${customer.referral_code}`); toast.success("Referral link copied."); }} className="text-xs bg-[#00B5D8] hover:bg-[#009AB8] text-black font-bold rounded-full px-4 py-2 transition-colors">
                  Copy share link
                </button>
              </div>
            )}
            <div className="mt-8 grid grid-cols-2 gap-4">
              <div className="bg-black/40 rounded-2xl p-4">
                <p className="text-xs text-white/50 uppercase tracking-widest">Your credit</p>
                <p className="mt-1 font-display text-3xl text-[#00B5D8]">{money(customer.referral_credit_eur || 0)}</p>
              </div>
              <div className="bg-black/40 rounded-2xl p-4">
                <p className="text-xs text-white/50 uppercase tracking-widest">Friends referred</p>
                <p className="mt-1 font-display text-3xl">{(customer.referral_history || []).length}</p>
              </div>
            </div>
            <p className="mt-6 text-xs text-white/40">Credit applies automatically to your next accessory purchase. One-time use per friend.</p>
          </div>
        </TabsContent>

        <TabsContent value="profile" className="mt-6 max-w-xl">
          <form onSubmit={saveProfile} className="bg-[#111] border border-white/10 rounded-2xl p-6 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-white/50">Name</label>
              <Input data-testid="profile-name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className="bg-white/5 border-white/10 mt-1" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-white/50">Phone</label>
              <Input data-testid="profile-phone" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} className="bg-white/5 border-white/10 mt-1" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-white/50">Birthday</label>
              <Input data-testid="profile-birthday" type="date" value={profile.birthday || ""} onChange={(e) => setProfile({ ...profile, birthday: e.target.value })} className="bg-white/5 border-white/10 mt-1 text-white" />
              <p className="text-xs text-white/50 mt-1">Used only to unlock your 15% birthday-month accessory discount.</p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-white/50">Email</label>
              <Input value={customer.email} disabled className="bg-white/5 border-white/10 mt-1 opacity-60" />
            </div>
            <Button data-testid="profile-save" type="submit" className="w-full bg-[#00B5D8] hover:bg-[#009AB8] text-black font-bold rounded-full py-6">Save profile</Button>
          </form>
        </TabsContent>
      </Tabs>
    </div>
  );
}
